import { OCRTextV1, TextFragment } from '@scale/shared/types/ocr';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { captureClientSideError } from '@scale/frontend-lib/errors';

interface UseTextFragmentsValue {
  textFragments: TextFragment[];
}

export function useTextFragments(ocrTextUrls: string[] | undefined): UseTextFragmentsValue {
  const [textFragments, setTextFragments] = useState<TextFragment[]>([]);
  useEffect(() => {
    if (!ocrTextUrls?.length) {
      return;
    }

    const signal = { aborted: false };
    Promise.all(ocrTextUrls.map(fetchTextFragments))
      .then(_.flatten)
      .then(ocrTextFragments => {
        if (!signal.aborted) {
          setTextFragments(ocrTextFragments);
        }
        return;
      })
      .catch(captureClientSideError);

    return () => {
      signal.aborted = true;
      setTextFragments([]);
    };
  }, [ocrTextUrls]);

  return { textFragments };
}

async function fetchTextFragments(ocrTextUrl: string): Promise<TextFragment[]> {
  if (!process.browser) {
    return [];
  }
  const response = await fetch(ocrTextUrl);
  if (!response.ok) {
    captureClientSideError(new Error(`failed to fetch ocrTextUrl status=${response.status}`));
    return [];
  }
  const payload: unknown = await response.json();
  if (!checkOCRTextV1(payload)) {
    captureClientSideError(new Error('invalid data returned from ocrTextUrl'));
    return [];
  }
  return payload.ocrTextFragments;
}

function checkOCRTextV1(response: unknown): response is OCRTextV1 {
  return _.get(response, 'version') === 1 && Array.isArray(_.get(response, 'ocrTextFragments'));
}
